import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { HottrMark } from '@/components/brand/HottrMark';
import { SectionMarker } from '@/components/brand/SectionMarker';
import { Icon } from '@/components/ui/Icon';
import { useAuth } from '@/modules/auth/useAuth';
import { useProfileBuilder } from '@/modules/auth/useProfileBuilder';
import { downscaleMany } from '@/modules/auth/photoUpload';
import { ROUTES } from '@/config/routes';
import type {
  Body,
  CockSize,
  Position,
  TonightWant,
  Tribe,
  VisibilityPreference,
} from '@/modules/auth/types';
import { cn } from '@/lib/cn';

const EASE = [0.19, 1, 0.22, 1] as const;

const STEPS = [
  'Name',
  'Photos',
  'Age',
  'Location',
  'Tonight',
  'Stats',
  'Interests',
] as const;
const TOTAL = STEPS.length;

const MAX_PHOTOS = 4;

const TONIGHT_OPTIONS: { value: TonightWant; label: string; line: string }[] = [
  { value: 'right_now', label: 'Right now', line: 'honest energy only.' },
  { value: 'good_company', label: 'Good company', line: 'somebody to share the late hours with.' },
  { value: 'out_for_it', label: 'Out for it', line: 'movement, music, whatever comes next.' },
  { value: 'just_looking', label: 'Just looking', line: 'no agenda. eyes open.' },
];

const SEE_OPTIONS: { value: VisibilityPreference; label: string }[] = [
  { value: 'men', label: 'Men' },
  { value: 'women', label: 'Women' },
  { value: 'trans', label: 'Trans' },
];

const BODY_OPTIONS: Body[] = [
  'lean', 'slim', 'average', 'athletic', 'fit', 'muscular', 'stocky', 'large', 'dad',
];

const POSITION_OPTIONS: Position[] = [
  'top', 'vers top', 'vers', 'vers bottom', 'bottom', 'side',
];

const COCK_OPTIONS: CockSize[] = ['small', 'average', 'large', 'xlarge', 'huge'];

const TRIBE_OPTIONS: Tribe[] = [
  'jock', 'otter', 'twink', 'bear', 'cub', 'daddy', 'leather', 'pup', 'geek',
];

const slugify = (name: string) =>
  name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 24);

/**
 * Profile builder — mandatory first-run flow after signup.
 *
 * Steps:
 *   1. Name — one field, handle auto-derived
 *   2. Photos — hero photo first; photos-first like leading apps
 *   3. Age — 18+ confirm + numeric age (text input, not number)
 *   4. Location — city / suburb / skip
 *   5. Tonight — what + who + optional mood line
 *   6. Stats — body / position / cock (all optional, feeds HEAT cold-start)
 *   7. Interests — tags + tribe (feeds HEAT surfacing)
 *
 * Identity rule (canon §12): never ask "what are you?" — only "who do
 * you want to see?" and "what are you looking for tonight?"
 */
export const ProfileBuilderPage = () => {
  const navigate = useNavigate();
  const { logOut } = useAuth();
  const { draft, isComplete, update, markCompleted, save } = useProfileBuilder();
  const [step, setStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const handleSignOut = async () => { await logOut(); };

  // Navigate to feed once the builder marks itself complete.
  // Auth is handled by ProtectedRoute — no duplicate gate here.
  useEffect(() => {
    if (isComplete && draft.completedAt) navigate(ROUTES.feed, { replace: true });
  }, [isComplete, draft.completedAt, navigate]);

  const next = () => setStep((s) => Math.min(s + 1, TOTAL));
  const back = () => setStep((s) => Math.max(s - 1, 1));

  const onFinish = async () => {
    if (!isComplete || saving) return;
    setSaveError(null);
    setSaving(true);
    const result = await save();
    setSaving(false);
    if (!result.ok) {
      setSaveError(result.error ?? 'Something went wrong. Try again.');
      return;
    }
    markCompleted();
  };

  const canAdvance = (() => {
    switch (step) {
      case 1: return draft.handle.trim().length >= 3;
      case 2: return draft.photos.length >= 1;
      case 3: return draft.ageConfirmed;
      case 4: return draft.location !== null || draft.locationPermissionDismissed;
      case 5: return draft.lookingForTonight !== null && draft.wantsToSee.length > 0;
      case 6: return true;
      case 7: return draft.interests.filter((i) => i.trim().length > 0).length >= 1;
      default: return false;
    }
  })();

  return (
    <div className="relative flex min-h-dvh flex-col bg-ink text-text-primary">
      {/* MASTHEAD */}
      <header
        className="flex items-center justify-between gap-4 border-b border-white/[0.07] px-6 py-4 md:px-10 md:py-5"
        style={{ paddingTop: 'max(1rem, env(safe-area-inset-top))' }}
      >
        <Link
          to={ROUTES.landing}
          aria-label="HOTTR."
          className="inline-flex min-h-[44px] items-center"
        >
          <HottrMark size="text-xl" surface=".app" />
        </Link>
        <div className="flex items-center gap-4">
          <span className="font-display text-[10px] font-bold uppercase tracking-widest text-text-muted">
            Step {String(step).padStart(2, '0')} / {String(TOTAL).padStart(2, '0')}
          </span>
          <button
            type="button"
            onClick={handleSignOut}
            className={cn(
              'inline-flex min-h-[44px] items-center px-1',
              'font-display text-[10px] font-bold uppercase tracking-widest text-text-muted',
              'transition-colors duration-fast ease-signal hover:text-pink',
            )}
          >
            Sign out
          </button>
        </div>
      </header>

      {/* PROGRESS BARS */}
      <div className="flex gap-1 px-6 pt-3 md:px-10">
        {STEPS.map((_, i) => (
          <span
            key={i}
            className={cn(
              'h-[2px] flex-1 transition-colors duration-fast ease-signal',
              i < step ? 'bg-pink shadow-glow-soft' : 'bg-white/[0.10]',
            )}
          />
        ))}
      </div>

      {/* CONTENT */}
      <motion.main
        key={step}
        initial={{ opacity: 0, y: 14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, ease: EASE }}
        className="mx-auto flex w-full max-w-2xl flex-1 flex-col px-6 py-10 md:px-10 md:py-14"
      >
        <SectionMarker className="mb-4">— {STEPS[step - 1]}</SectionMarker>

        {step === 1 && (
          <StepName
            displayName={draft.displayName}
            handle={draft.handle}
            onDisplay={(v) => {
              const name = v.slice(0, 40);
              update({ displayName: name, handle: slugify(name) });
            }}
          />
        )}

        {step === 2 && (
          <StepPhotos
            photos={draft.photos}
            onAddPhotos={async (files) => {
              const results = await downscaleMany(files);
              const ok = results.filter((r) => r.ok && r.dataUrl).map((r) => r.dataUrl as string);
              if (ok.length === 0) return;
              update({ photos: [...draft.photos, ...ok].slice(0, MAX_PHOTOS) });
            }}
            onRemovePhoto={(idx) => update({ photos: draft.photos.filter((_, i) => i !== idx) })}
            onPromote={(idx) => {
              if (idx === 0) return;
              const next = [...draft.photos];
              const [moved] = next.splice(idx, 1);
              next.unshift(moved);
              update({ photos: next });
            }}
          />
        )}

        {step === 3 && (
          <StepAge
            ageConfirmed={draft.ageConfirmed}
            age={draft.age}
            onToggle={(v) => update({ ageConfirmed: v })}
            onAge={(v) => update({ age: v })}
          />
        )}

        {step === 4 && (
          <StepLocation
            location={draft.location}
            dismissed={draft.locationPermissionDismissed}
            onPick={(v) => update({ location: v, locationPermissionDismissed: false })}
            onDismiss={() => update({ location: null, locationPermissionDismissed: true })}
          />
        )}

        {step === 5 && (
          <StepTonight
            tonight={draft.lookingForTonight}
            see={draft.wantsToSee}
            mood={draft.mood}
            onTonight={(v) => update({ lookingForTonight: v })}
            onToggleSee={(v) =>
              update({
                wantsToSee: draft.wantsToSee.includes(v)
                  ? draft.wantsToSee.filter((x) => x !== v)
                  : [...draft.wantsToSee, v],
              })
            }
            onMood={(v) => update({ mood: v.slice(0, 80) })}
          />
        )}

        {step === 6 && (
          <StepStats
            height={draft.height}
            weight={draft.weight}
            body={draft.body}
            position={draft.position}
            cockSize={draft.cockSize}
            onHeight={(v) => update({ height: v.slice(0, 20) })}
            onWeight={(v) => update({ weight: v.slice(0, 20) })}
            onBody={(v) => update({ body: v })}
            onPosition={(v) => update({ position: v })}
            onCock={(v) => update({ cockSize: v })}
          />
        )}

        {step === 7 && (
          <StepInterests
            interests={draft.interests}
            tribe={draft.tribe}
            onInterest={(idx, value) => {
              const next = [...draft.interests];
              while (next.length < 3) next.push('');
              next[idx] = value.toLowerCase().slice(0, 24);
              update({ interests: next });
            }}
            onTribe={(v) => update({ tribe: v })}
          />
        )}

        {/* NAV */}
        <div
          className="mt-auto flex items-center justify-between gap-3 pt-12"
          style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
        >
          <button
            type="button"
            onClick={back}
            disabled={step === 1}
            className={cn(
              'inline-flex min-h-[44px] items-center gap-1.5 px-3 font-display text-[11px] font-bold uppercase tracking-widest',
              'transition-colors duration-fast ease-signal',
              step === 1
                ? 'cursor-not-allowed text-text-muted opacity-40'
                : 'text-text-secondary hover:text-pink',
            )}
          >
            <Icon name="arrow_back" size={14} />
            Back
          </button>

          {step < TOTAL ? (
            <button
              type="button"
              onClick={next}
              disabled={!canAdvance}
              className={cn(
                'inline-flex min-h-[44px] items-center gap-2 border px-5 py-3',
                'font-display text-[11px] font-black uppercase tracking-widest',
                'transition-[border-color,background,color] duration-fast ease-signal',
                canAdvance
                  ? 'border-pink bg-pink/[0.10] text-pink shadow-glow-soft hover:bg-pink/[0.18]'
                  : 'cursor-not-allowed border-white/[0.10] text-text-muted',
              )}
            >
              Next
              <Icon name="arrow_forward" size={14} />
            </button>
          ) : (
            <div className="flex flex-col items-end gap-3">
              <button
                type="button"
                onClick={onFinish}
                disabled={!isComplete || saving}
                className={cn(
                  'inline-flex min-h-[44px] items-center gap-2 border px-5 py-3',
                  'font-display text-[11px] font-black uppercase tracking-widest',
                  'transition-[border-color,background,color] duration-fast ease-signal',
                  isComplete && !saving
                    ? 'border-pink bg-pink/[0.10] text-pink shadow-glow-soft hover:bg-pink/[0.18]'
                    : 'cursor-not-allowed border-white/[0.10] text-text-muted',
                )}
              >
                {saving ? 'Saving' : 'Enter HOTTR'}
                <span aria-hidden className="text-pink">.</span>
                <Icon name={saving ? 'hourglass_empty' : 'arrow_forward'} size={14} />
              </button>
              {saveError && (
                <p className="font-display text-[10px] font-bold uppercase tracking-widest text-pink">
                  — {saveError}
                </p>
              )}
            </div>
          )}
        </div>
      </motion.main>
    </div>
  );
};

/* ============================================================
   STEP COMPONENTS
   ============================================================ */

const StepShell = ({
  heading,
  intro,
  children,
}: {
  heading: ReactNode;
  intro?: ReactNode;
  children: ReactNode;
}) => (
  <>
    <h1
      className="font-display font-black uppercase tracking-display"
      style={{
        fontSize: 'clamp(36px, 5vw, 64px)',
        lineHeight: 0.92,
        letterSpacing: '-0.045em',
      }}
    >
      {heading}
    </h1>
    {intro && (
      <p className="mt-4 max-w-xl text-sm leading-relaxed text-text-secondary md:text-base">
        {intro}
      </p>
    )}
    <div className="mt-9">{children}</div>
  </>
);

const StepName = ({
  displayName,
  handle,
  onDisplay,
}: {
  displayName: string;
  handle: string;
  onDisplay: (v: string) => void;
}) => (
  <StepShell
    heading={<>What's your name<span className="text-pink">?</span></>}
    intro="What people see on your profile. As you'd introduce yourself."
  >
    <FieldRow
      label="Name"
      hint={handle.length >= 3 ? `Your handle: @${handle}` : 'At least 3 characters to continue.'}
    >
      <input
        value={displayName}
        onChange={(e) => onDisplay(e.target.value)}
        autoFocus
        placeholder="Kasper"
        maxLength={40}
        className={cn(
          'w-full bg-transparent px-1 font-display text-2xl font-black uppercase tracking-tight text-text-primary md:text-3xl',
          'focus:outline-none placeholder:text-text-muted',
        )}
        style={{ letterSpacing: '-0.04em' }}
      />
    </FieldRow>
  </StepShell>
);

const StepPhotos = ({
  photos,
  onAddPhotos,
  onRemovePhoto,
  onPromote,
}: {
  photos: string[];
  onAddPhotos: (files: FileList) => Promise<void>;
  onRemovePhoto: (idx: number) => void;
  onPromote: (idx: number) => void;
}) => {
  const fileRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const remaining = MAX_PHOTOS - photos.length;

  const handlePicked = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    setBusy(true);
    await onAddPhotos(files);
    setBusy(false);
    if (fileRef.current) fileRef.current.value = '';
  };

  return (
    <StepShell
      heading={<>Set the room<span className="text-pink">.</span></>}
      intro={`Your first photo is the hero. Tap any other to promote it. Up to ${MAX_PHOTOS} photos.`}
    >
      <div>
        <div className="mb-2 flex items-baseline justify-between">
          <span className="font-display text-[10px] font-bold uppercase tracking-widest text-text-muted">
            — Photos ({photos.length} / {MAX_PHOTOS})
          </span>
          {remaining > 0 && (
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              disabled={busy}
              className={cn(
                'font-display text-[10px] font-bold uppercase tracking-widest',
                'transition-colors duration-fast ease-signal',
                busy ? 'text-text-muted opacity-60' : 'text-pink hover:text-pink',
              )}
            >
              {busy ? 'Adding...' : 'Add photo'}
            </button>
          )}
        </div>

        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          multiple
          className="hidden"
          onChange={(e) => handlePicked(e.target.files)}
        />

        <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
          {Array.from({ length: MAX_PHOTOS }).map((_, i) => {
            const photo = photos[i];
            if (photo) {
              return (
                <div
                  key={i}
                  className={cn(
                    'relative aspect-[4/5] overflow-hidden border',
                    i === 0 ? 'border-pink shadow-glow-soft' : 'border-white/[0.12]',
                  )}
                >
                  <img
                    src={photo}
                    alt=""
                    className="hottr-matte-editorial absolute inset-0 h-full w-full object-cover"
                  />
                  {i === 0 && (
                    <span className="absolute left-1.5 top-1.5 border border-pink/40 bg-ink/85 px-1.5 py-0.5 font-display text-[8px] font-bold uppercase tracking-widest text-pink backdrop-blur">
                      Hero
                    </span>
                  )}
                  <div className="absolute inset-x-0 bottom-0 flex items-center justify-between bg-ink/85 px-2 py-1 backdrop-blur">
                    {i === 0 ? (
                      <span className="font-display text-[9px] font-bold uppercase tracking-widest text-text-muted">
                        —
                      </span>
                    ) : (
                      <button
                        type="button"
                        onClick={() => onPromote(i)}
                        className="font-display text-[9px] font-bold uppercase tracking-widest text-text-secondary hover:text-pink"
                      >
                        Make hero
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => onRemovePhoto(i)}
                      aria-label={`Remove photo ${i + 1}`}
                      className="inline-flex h-5 w-5 items-center justify-center text-text-secondary hover:text-pink"
                    >
                      <Icon name="close" size={12} />
                    </button>
                  </div>
                </div>
              );
            }
            return (
              <button
                key={i}
                type="button"
                onClick={() => fileRef.current?.click()}
                disabled={busy}
                className={cn(
                  'relative aspect-[4/5] overflow-hidden border border-dashed border-white/[0.15] bg-charcoal/40',
                  'flex flex-col items-center justify-center gap-2',
                  'transition-[border-color,background] duration-fast ease-signal',
                  'hover:border-pink/40 hover:bg-pink/[0.04]',
                  'disabled:cursor-not-allowed disabled:opacity-50',
                )}
              >
                <Icon name="add_a_photo" size={24} className="text-text-muted" />
                <span className="font-display text-[9px] font-bold uppercase tracking-widest text-text-muted">
                  Add photo
                </span>
              </button>
            );
          })}
        </div>
        <p className="mt-2 font-display text-[9px] font-bold uppercase tracking-widest text-text-muted">
          Auto-resized to keep things light<span className="text-pink">.</span>
        </p>
      </div>
    </StepShell>
  );
};

const StepAge = ({
  ageConfirmed,
  age,
  onToggle,
  onAge,
}: {
  ageConfirmed: boolean;
  age: number | null;
  onToggle: (v: boolean) => void;
  onAge: (v: number | null) => void;
}) => (
  <StepShell
    heading={<>18+ only<span className="text-pink">.</span></>}
    intro="HOTTR is for adults. Confirm you're 18 or older. Your age is shown on your profile — it's the only number that has to be true."
  >
    <div className="grid gap-5">
      <button
        type="button"
        onClick={() => onToggle(!ageConfirmed)}
        aria-pressed={ageConfirmed}
        className={cn(
          'flex w-full items-start gap-4 border px-5 py-5 text-left',
          'font-display text-base font-bold uppercase tracking-tight',
          'transition-[border-color,background,color] duration-fast ease-signal',
          ageConfirmed
            ? 'border-pink bg-pink/[0.06] text-pink shadow-glow-soft'
            : 'border-white/[0.12] text-text-primary hover:border-pink/40',
        )}
      >
        <span
          aria-hidden
          className={cn(
            'mt-0.5 inline-flex h-6 w-6 shrink-0 items-center justify-center border',
            ageConfirmed ? 'border-pink bg-pink/[0.20]' : 'border-white/[0.20]',
          )}
        >
          {ageConfirmed && <Icon name="check" size={16} />}
        </span>
        <span className="leading-snug">
          I'm 18 or over<span className="text-pink">.</span>
          <span className="ml-2 font-display text-[10px] font-bold uppercase tracking-widest text-text-muted">
            Required.
          </span>
        </span>
      </button>

      <FieldRow label="Your age" hint="Shown on your profile.">
        <input
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          maxLength={2}
          value={age ?? ''}
          onChange={(e) => {
            const raw = e.target.value.replace(/[^0-9]/g, '');
            if (raw === '') { onAge(null); return; }
            const n = parseInt(raw, 10);
            onAge(Number.isNaN(n) ? null : n);
          }}
          placeholder="28"
          className={cn(
            'w-full bg-transparent px-1 font-display text-2xl font-black uppercase tracking-tight text-text-primary md:text-3xl',
            'focus:outline-none placeholder:text-text-muted',
          )}
          style={{ letterSpacing: '-0.04em' }}
        />
      </FieldRow>
    </div>
  </StepShell>
);

const STARTER_CITIES = ['Melbourne', 'Sydney', 'Brisbane', 'Perth', 'Adelaide', 'Auckland'];

const StepLocation = ({
  location,
  dismissed,
  onPick,
  onDismiss,
}: {
  location: string | null;
  dismissed: boolean;
  onPick: (v: string) => void;
  onDismiss: () => void;
}) => {
  const [custom, setCustom] = useState(location ?? '');

  return (
    <StepShell
      heading={<>Where are you<span className="text-pink">?</span></>}
      intro="Used to surface what's near you tonight. Pick a city or type your suburb. You can change this later, or skip it for now."
    >
      <div className="grid gap-5">
        <div className="grid grid-cols-2 gap-2 md:grid-cols-3">
          {STARTER_CITIES.map((city) => (
            <button
              key={city}
              type="button"
              onClick={() => onPick(city)}
              aria-pressed={location === city}
              className={cn(
                'border px-3 py-4 text-left font-display text-sm font-bold uppercase tracking-tight',
                'transition-[border-color,background,color] duration-fast ease-signal',
                location === city
                  ? 'border-pink bg-pink/[0.06] text-pink shadow-glow-soft'
                  : 'border-white/[0.12] text-text-primary hover:border-pink/40',
              )}
            >
              {city}
            </button>
          ))}
        </div>

        <FieldRow label="Or type a suburb">
          <input
            value={custom}
            onChange={(e) => {
              setCustom(e.target.value);
              if (e.target.value.trim()) onPick(e.target.value.trim());
            }}
            placeholder="Carlton"
            maxLength={40}
            className={cn(
              'w-full bg-transparent px-1 font-display text-xl font-black uppercase tracking-tight text-text-primary md:text-2xl',
              'focus:outline-none placeholder:text-text-muted',
            )}
            style={{ letterSpacing: '-0.04em' }}
          />
        </FieldRow>

        <button
          type="button"
          onClick={onDismiss}
          className={cn(
            'inline-flex items-center gap-1.5 self-start font-display text-[10px] font-bold uppercase tracking-widest',
            dismissed ? 'text-pink' : 'text-text-muted hover:text-pink',
          )}
        >
          {dismissed ? '✓ Skipped — add later' : 'Skip for now'}
        </button>
      </div>
    </StepShell>
  );
};

const StepTonight = ({
  tonight,
  see,
  mood,
  onTonight,
  onToggleSee,
  onMood,
}: {
  tonight: TonightWant | null;
  see: VisibilityPreference[];
  mood: string;
  onTonight: (v: TonightWant) => void;
  onToggleSee: (v: VisibilityPreference) => void;
  onMood: (v: string) => void;
}) => (
  <StepShell
    heading={<>Tonight<span className="text-pink">.</span></>}
    intro="What are you looking for, and who do you want to see? Preferences, not declarations — you're telling us how to shape the feed, not labelling yourself."
  >
    <section className="grid gap-3">
      <p className="font-display text-[10px] font-bold uppercase tracking-widest text-text-muted">
        — What are you looking for tonight?
      </p>
      <div className="grid gap-2 md:grid-cols-2">
        {TONIGHT_OPTIONS.map((opt) => {
          const active = tonight === opt.value;
          return (
            <button
              key={opt.value}
              type="button"
              onClick={() => onTonight(opt.value)}
              aria-pressed={active}
              className={cn(
                'border px-4 py-4 text-left',
                'transition-[border-color,background,color] duration-fast ease-signal',
                active
                  ? 'border-pink bg-pink/[0.06] text-pink shadow-glow-soft'
                  : 'border-white/[0.12] text-text-primary hover:border-pink/40',
              )}
            >
              <span className="block font-display text-base font-black uppercase tracking-tight">
                {opt.label}
              </span>
              <span
                className={cn(
                  'mt-1 block text-xs italic leading-relaxed',
                  active ? 'text-pink/80' : 'text-text-secondary',
                )}
              >
                {opt.line}
              </span>
            </button>
          );
        })}
      </div>
    </section>

    <section className="mt-6 grid gap-3">
      <p className="font-display text-[10px] font-bold uppercase tracking-widest text-text-muted">
        — Who do you want to see tonight?{' '}
        <span className="text-text-secondary">Multi-select. Preference, not identity.</span>
      </p>
      <div className="grid grid-cols-3 gap-2">
        {SEE_OPTIONS.map((opt) => {
          const active = see.includes(opt.value);
          return (
            <button
              key={opt.value}
              type="button"
              onClick={() => onToggleSee(opt.value)}
              aria-pressed={active}
              className={cn(
                'border px-3 py-4 text-center',
                'font-display text-base font-black uppercase tracking-tight',
                'transition-[border-color,background,color] duration-fast ease-signal',
                active
                  ? 'border-pink bg-pink/[0.06] text-pink shadow-glow-soft'
                  : 'border-white/[0.12] text-text-primary hover:border-pink/40',
              )}
            >
              {opt.label}
            </button>
          );
        })}
      </div>
    </section>

    <section className="mt-6">
      <FieldRow label="Mood — optional" hint="One line. Lowercase. Skip if you're not feeling it.">
        <input
          value={mood}
          onChange={(e) => onMood(e.target.value)}
          placeholder="late shift, slow burn"
          maxLength={80}
          className={cn(
            'w-full bg-transparent px-1 font-display text-xl italic tracking-tight text-text-primary md:text-2xl',
            'focus:outline-none placeholder:text-text-muted',
          )}
        />
      </FieldRow>
    </section>
  </StepShell>
);

const StepStats = ({
  height,
  weight,
  body,
  position,
  cockSize,
  onHeight,
  onWeight,
  onBody,
  onPosition,
  onCock,
}: {
  height: string;
  weight: string;
  body: Body | null;
  position: Position | null;
  cockSize: CockSize | null;
  onHeight: (v: string) => void;
  onWeight: (v: string) => void;
  onBody: (v: Body) => void;
  onPosition: (v: Position) => void;
  onCock: (v: CockSize) => void;
}) => (
  <StepShell
    heading={<>The basics<span className="text-pink">.</span></>}
    intro="Quick stats so the feed knows what to surface. All optional — fill what you're happy showing, skip the rest."
  >
    <div className="grid gap-5">
      <div className="grid grid-cols-2 gap-3">
        <FieldRow label="Height">
          <input
            value={height}
            onChange={(e) => onHeight(e.target.value)}
            placeholder="5'11&quot;"
            className={cn(
              'w-full bg-transparent px-1 font-display text-xl font-black uppercase tracking-tight text-text-primary md:text-2xl',
              'focus:outline-none placeholder:text-text-muted',
            )}
            style={{ letterSpacing: '-0.04em' }}
          />
        </FieldRow>
        <FieldRow label="Weight">
          <input
            value={weight}
            onChange={(e) => onWeight(e.target.value)}
            placeholder="78 kg"
            className={cn(
              'w-full bg-transparent px-1 font-display text-xl font-black uppercase tracking-tight text-text-primary md:text-2xl',
              'focus:outline-none placeholder:text-text-muted',
            )}
            style={{ letterSpacing: '-0.04em' }}
          />
        </FieldRow>
      </div>

      <ChipGroup<Body>
        label="Body"
        options={BODY_OPTIONS}
        value={body}
        onChange={onBody}
      />

      <ChipGroup<Position>
        label="Position"
        options={POSITION_OPTIONS}
        value={position}
        onChange={onPosition}
        cols={3}
      />

      <ChipGroupResponsive<CockSize>
        label="Cock"
        options={COCK_OPTIONS}
        value={cockSize}
        onChange={onCock}
      />
    </div>
  </StepShell>
);

const StepInterests = ({
  interests,
  tribe,
  onInterest,
  onTribe,
}: {
  interests: string[];
  tribe: Tribe | null;
  onInterest: (idx: number, value: string) => void;
  onTribe: (v: Tribe) => void;
}) => {
  const padded = [interests[0] ?? '', interests[1] ?? '', interests[2] ?? ''];

  return (
    <StepShell
      heading={<>What you're into<span className="text-pink">.</span></>}
      intro="Three short tags. Lowercase. Specific. The more honest, the more the feed reads you. Pick a tribe if one fits — skip if not."
    >
      <div className="grid gap-5">
        <div className="grid gap-3">
          <span className="font-display text-[10px] font-bold uppercase tracking-widest text-text-muted">
            — Interests
          </span>
          {padded.map((value, i) => (
            <FieldRow
              key={i}
              label={`Tag ${String(i + 1).padStart(2, '0')}`}
              hint={i === 0 ? 'At least one required.' : undefined}
            >
              <input
                value={value}
                onChange={(e) => onInterest(i, e.target.value)}
                placeholder={['cooking', 'records', 'late walks'][i]}
                maxLength={24}
                className={cn(
                  'w-full bg-transparent px-1 font-display text-lg tracking-tight text-text-primary md:text-xl',
                  'focus:outline-none placeholder:text-text-muted',
                )}
              />
            </FieldRow>
          ))}
        </div>

        <ChipGroup<Tribe>
          label="Tribe (optional)"
          options={TRIBE_OPTIONS}
          value={tribe}
          onChange={onTribe}
          cols={3}
        />
      </div>
    </StepShell>
  );
};

/* ============================================================
   SHARED FIELD + CHIP HELPERS
   ============================================================ */

const FieldRow = ({
  label,
  hint,
  error,
  children,
}: {
  label: string;
  hint?: string;
  error?: string | null;
  children: ReactNode;
}) => (
  <label className="block border-b border-white/[0.12] pb-3">
    <span className="block font-display text-[10px] font-bold uppercase tracking-widest text-text-muted">
      {label}
    </span>
    <div className="mt-2">{children}</div>
    {error ? (
      <span className="mt-1 block font-display text-[10px] font-bold uppercase tracking-widest text-pink">
        — {error}
      </span>
    ) : hint ? (
      <span className="mt-1 block font-display text-[10px] font-bold uppercase tracking-widest text-text-muted">
        {hint}
      </span>
    ) : null}
  </label>
);

function ChipGroupResponsive<T extends string>({
  label,
  options,
  value,
  onChange,
}: {
  label: string;
  options: readonly T[];
  value: T | null;
  onChange: (v: T) => void;
}) {
  return (
    <div className="grid gap-2">
      <span className="font-display text-[10px] font-bold uppercase tracking-widest text-text-muted">
        — {label}
      </span>
      <div className="grid grid-cols-3 gap-2 sm:grid-cols-5">
        {options.map((opt) => {
          const active = value === opt;
          return (
            <button
              key={opt}
              type="button"
              onClick={() => onChange(opt)}
              aria-pressed={active}
              className={cn(
                'flex min-h-[44px] items-center justify-center border px-2 py-2 text-center',
                'font-display text-[11px] font-black uppercase tracking-widest',
                'transition-[border-color,background,color] duration-fast ease-signal',
                active
                  ? 'border-pink bg-pink/[0.06] text-pink shadow-glow-soft'
                  : 'border-white/[0.12] text-text-primary hover:border-pink/40',
              )}
            >
              {opt}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function ChipGroup<T extends string>({
  label,
  options,
  value,
  onChange,
  cols = 3,
}: {
  label: string;
  options: readonly T[];
  value: T | null;
  onChange: (v: T) => void;
  cols?: 2 | 3 | 4 | 5;
}) {
  const colsClass: Record<2 | 3 | 4 | 5, string> = {
    2: 'grid-cols-2',
    3: 'grid-cols-3',
    4: 'grid-cols-4',
    5: 'grid-cols-5',
  };

  return (
    <div className="grid gap-2">
      <span className="font-display text-[10px] font-bold uppercase tracking-widest text-text-muted">
        — {label}
      </span>
      <div className={cn('grid gap-2', colsClass[cols])}>
        {options.map((opt) => {
          const active = value === opt;
          return (
            <button
              key={opt}
              type="button"
              onClick={() => onChange(opt)}
              aria-pressed={active}
              className={cn(
                'flex min-h-[44px] items-center justify-center border px-2 py-2 text-center',
                'font-display text-[11px] font-black uppercase tracking-widest',
                'transition-[border-color,background,color] duration-fast ease-signal',
                active
                  ? 'border-pink bg-pink/[0.06] text-pink shadow-glow-soft'
                  : 'border-white/[0.12] text-text-primary hover:border-pink/40',
              )}
            >
              {opt}
            </button>
          );
        })}
      </div>
    </div>
  );
}
